import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DatePicker, Button, message, Modal, Form, Input, Checkbox, Upload, Spin, Row, Col } from 'antd';
import { ReloadOutlined, PlusOutlined, UploadOutlined, DownloadOutlined, CloseOutlined, CheckCircleOutlined, DollarOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import styles from './Dashboard.module.css';
import dayjs from 'dayjs';
import axios from '../../utils/axios';
import { translateBackendMessage } from '../../utils/translateMessage';
// import { fetchDraftReports, setCurrentDraftReport, setCurrentDraftTemplate, setCurrentDraftReportNumber, updateReport, createReport, deleteReport } from '../../redux/slices/reportsSlice';
import { GridComponent, ColumnsDirective, ColumnDirective, Page, Inject, Edit, Toolbar, Filter, Sort, CommandColumn, ExcelExport, Selection } from '@syncfusion/ej2-react-grids';
import { DropDownList } from '@syncfusion/ej2-dropdowns';
import { ComboBoxComponent } from '@syncfusion/ej2-react-dropdowns';
import {
  fetchRequests,
  updateRequestStatus,
  createRequest,
  fetchRequestWarnings,
  uploadFiles,
  fetchRequestFiles,
  deleteRequestFile,
  fetchFileDownloadUrl,
  clearFiles,
  clearDownloadUrl,
} from '../../redux/slices/requestsSlice';
import { fetchUsers } from '../../redux/slices/usersSlice';
const { RangePicker } = DatePicker;

const SIGNATURE_ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png';
const SIGNATURE_EXTENSIONS = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];
const SIGNATURE_MAX_BYTES = 10 * 1024 * 1024;
const FILE_KIND_SGN = 'SGN';

const getSignatureFileFromList = (fileList) => {
  const item = Array.isArray(fileList) ? fileList[0] : null;
  if (!item) return null;
  if (item.originFileObj) return item.originFileObj;
  return item instanceof File ? item : null;
};

const isSignatureKind = (kind) => kind === FILE_KIND_SGN || kind === 'SIGNATURE';

const getFileExtension = (name) => {
  const parts = String(name || '').split('.');
  if (parts.length < 2) return '';
  return `.${parts.pop().toLowerCase()}`;
};

const isImageFileName = (name) => ['.jpg', '.jpeg', '.png'].includes(getFileExtension(name));
const isPdfFileName = (name) => getFileExtension(name) === '.pdf';

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { rqst_sn: rqstSnParam } = useParams();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const originalStatusByRqstSnRef = useRef({});
  const approvalRequestSentRef = useRef(false);
  const authToken = useSelector((state) => state.auth?.token);
  const role = useSelector((state) => state.auth?.user?.role);
  const isPrivileged = role === 'ADMIN' || role === 'OWNER' || role === 'STAFF';
  const isAdminOrOwner = role === 'ADMIN' || role === 'OWNER';
  const { requestsList, loading, creating, warnings, warningsLoading, error, files, filesLoading } = useSelector((state) => state.requests);
  const { usersList, loading: usersLoading } = useSelector((state) => state.users || { usersList: [], loading: false });
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isResponseDialogOpen, setIsResponseDialogOpen] = useState(false);
  const [isRequestFilesDialogOpen, setIsRequestFilesDialogOpen] = useState(false);
  const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [signaturePreviewLoading, setSignaturePreviewLoading] = useState(false);
  const [createForm] = Form.useForm();
  const [currFiles, setCurrFiles] = useState(null);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  // Prevent double-submit (double click / Enter) creating duplicate requests
  const createRequestSubmitLockRef = useRef(false);
  const [isSubmittingCreateRequest, setIsSubmittingCreateRequest] = useState(false);
  const [selectedRqstSn, setSelectedRqstSn] = useState(null);
  const responseFileInputRef = useRef(null);
  const resetResponseUploadState = () => {
    setCurrFiles(null);
    setSelectedRqstSn(null);
    if (responseFileInputRef.current) {
      responseFileInputRef.current.value = '';
    }
  };

  const isValidIsraeliId = (value) => {
    const str = String(value || '').trim();
    // Must be exactly 9 digits (short IDs must be entered with leading zeros)
    if (!/^\d{9}$/.test(str)) return false;
    const id = str;
    // Reject trivial all-same-digit IDs (common invalid input)
    if (/^(\d)\1{8}$/.test(id)) return false;

    let sum = 0;
    for (let i = 0; i < 9; i += 1) {
      const num = Number(id[i]);
      const mult = (i % 2) + 1; // 1,2,1,2...
      let prod = num * mult;
      if (prod > 9) prod = Math.floor(prod / 10) + (prod % 10);
      sum += prod;
    }
    return sum % 10 === 0;
  };

  const MODAL_OPEN_DELAY_MS = 250;
  const modalOpenTimeoutsRef = useRef([]);

  const approvalToken = useMemo(() => {
    const tokenParam = new URLSearchParams(location.search).get('token');
    return tokenParam ? tokenParam.trim() : '';
  }, [location.search]);

  const approvalRqstSn = useMemo(() => {
    const parsed = Number(rqstSnParam);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [rqstSnParam]);

  const isApprovalLink = Boolean(approvalRqstSn && approvalToken);

  const openModalWithDelay = (setter) => {
    const timeoutId = setTimeout(() => {
      setter(true);
    }, MODAL_OPEN_DELAY_MS);
    modalOpenTimeoutsRef.current.push(timeoutId);
  };

  useEffect(() => {
    return () => {
      modalOpenTimeoutsRef.current.forEach((id) => clearTimeout(id));
      modalOpenTimeoutsRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!isApprovalLink || approvalRequestSentRef.current) return;
    approvalRequestSentRef.current = true;

    axios.get(`/requests/${approvalRqstSn}/approve`, {
      params: { token: approvalToken },
    }).then((response) => {
      const responseMessage = response?.data?.message;
      const translated = translateBackendMessage(responseMessage);
      message.success(translated || t('requests.approvedSuccess', { defaultValue: 'Request approved successfully' }));
    }).catch((error) => {
      console.error('Approval request failed', error);
    });
  }, [approvalRqstSn, approvalToken, isApprovalLink, t]);

  const handleRequestFileDownload = (file) => {
    dispatch(fetchFileDownloadUrl({ fileUuid: file.uuid })).unwrap().then((response) => {
      window.open(response.data.downloadUrl);
    }).catch((error) => {
      console.error(error);
      const translated = translateBackendMessage(error?.message);
      message.error(translated || error?.message);
    });
    dispatch(fetchRequests(isPrivileged ? { startDate, endDate } : {}));
  };

  const handleRequestFileDelete = (file) => {
    if (!file?.uuid) return;
    dispatch(deleteRequestFile({ fileUuid: file.uuid })).unwrap().then(() => {
      message.success(t('requests.fileDeleted') || 'File deleted');
    }).catch((error) => {
      console.error(error);
      const translated = translateBackendMessage(error?.message);
      message.error(translated || error?.message || t('notifications.frontend.dashboard.deleteFileFailed'));
    });
  };

  const resetSignaturePreview = () => {
    setSignaturePreview(null);
    setSignaturePreviewLoading(false);
    dispatch(clearDownloadUrl());
  };

  const openSignaturePreview = async (rqstSn) => {
    setSignaturePreview(null);
    if (!rqstSn) return;
    setSignaturePreviewLoading(true);
    try {
      const listRes = await dispatch(fetchRequestFiles({ rqstSn })).unwrap();
      const signatureFile = (listRes?.data || []).find((file) => isSignatureKind(file.kind));
      if (!signatureFile?.uuid) {
        setSignaturePreview(null);
        return;
      }
      const urlRes = await dispatch(fetchFileDownloadUrl({
        fileUuid: signatureFile.uuid,
        inline: true,
      })).unwrap();
      setSignaturePreview({
        uuid: signatureFile.uuid,
        name: signatureFile.originalName,
        url: urlRes?.data?.downloadUrl || null,
      });
    } catch (error) {
      const msg = typeof error === 'string' ? error : (error?.message || error?.error);
      const translated = translateBackendMessage(msg);
      message.error(
        translated || msg || t('notifications.frontend.dashboard.fetchSignatureFailed')
      );
      setSignaturePreview(null);
    } finally {
      setSignaturePreviewLoading(false);
    }
  };

  const handleSignatureDownload = (file) => {
    if (!file?.uuid) return;
    dispatch(fetchFileDownloadUrl({ fileUuid: file.uuid })).unwrap().then((response) => {
      window.open(response.data.downloadUrl);
    }).catch((error) => {
      const msg = typeof error === 'string' ? error : (error?.message || error?.error);
      const translated = translateBackendMessage(msg);
      message.error(translated || msg);
    });
  };

  const signatureCommand = {
    type: 'View Signature',
    headerText: t('requests.signature') || 'Signature',
    buttonOption: { iconCss: 'e-icons signature-cmd', cssClass: 'e-flat' },
  };
  const officeCommands = [
    signatureCommand,
    { type: 'Request Files', headerText: t('requests.files'), buttonOption: { iconCss: 'e-icons e-file-document', cssClass: 'e-flat' } },
  ];
  const commands = [
    signatureCommand,
    { type: 'Update Response', headerText: t('common.upload'), buttonOption: { iconCss: 'e-icons e-upload-1', cssClass: 'e-flat' } },
    { type: 'Request Files', headerText: t('requests.files'), buttonOption: { iconCss: 'e-icons e-file-document', cssClass: 'e-flat' } },
  ];

  // DEFAULT DATE RANGE: last 3 months → today (dayjs version)
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(3, 'month'),
    dayjs(),
  ]);

  const startDate = useMemo(() => dateRange?.[0]?.format('YYYY-MM-DD'), [dateRange]);
  const endDate = useMemo(() => dateRange?.[1]?.format('YYYY-MM-DD'), [dateRange]);

  const formatIsraelDateTime = (value) => {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    // Convert & format in Israel time zone
    const formatted = date.toLocaleString('en-GB', {
      timeZone: 'Asia/Jerusalem',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      // second: '2-digit',
      hour12: false,
    });

    // en-GB often returns "DD/MM/YYYY, HH:mm:ss" → remove the comma
    return formatted.replace(',', '');
  };

  const israelDateValueAccessor = (field, data) => formatIsraelDateTime(data?.[field]);
  const officeOwnerValueAccessor = (_field, data) => data?.officeOwner || data?.user?.ofc_owner || '-';
  const fullNameValueAccessor = (_field, data) => {
    const first = (data?.rqst_prsn_first || '').trim();
    const last = (data?.rqst_prsn_last || '').trim();
    const full = `${first} ${last}`.trim();
    return full || '-';
  };
  const duplicateValueAccessor = (_field, data) => {
    const dup = Number(data?.request_duplicate || 0);
    if (dup === 1) {
      return `1 - ${t('requests.duplicateSameOffice', { defaultValue: 'Duplicate – Same Office' })}`;
    }
    if (dup === 2) {
      return `2 - ${t('requests.duplicateDifferentOffice', { defaultValue: 'Duplicate – Different Office' })}`;
    }
    return '0';
  };

  const REQUEST_STATUSES = useMemo(
    () => ['OPEN', 'CANCELLED', 'SENT', 'RESPONSE', 'APPROVED', 'PAID', 'ID_NOT_VALID'],
    []
  );

  const statusLookup = useMemo(
    () =>
      REQUEST_STATUSES.map((value) => ({
        value,
        text: t(`status.${String(value).toLowerCase()}`, { defaultValue: value }),
      })),
    [REQUEST_STATUSES, t]
  );

  const getStatusText = (value) =>
    t(`status.${String(value).toLowerCase()}`, { defaultValue: value });

  const getStatusValueFromText = (text) => {
    const found = statusLookup.find((x) => x.text === text);
    return found?.value ?? text;
  };

  const getAllowedStatusOptions = (row) => {
    const current = row?.rqst_status;
    if (role === 'OFFICE') {
      if (current === 'OPEN') return ['CANCELLED'];
      if (current === 'RESPONSE') return ['APPROVED'];
      return [];
    }
    if (role === 'ADMIN' || role === 'STAFF' || role === 'OWNER') {
      return current === 'CANCELLED' ? [] : REQUEST_STATUSES;
    }
    return [];
  };

  const getStatusDropdownOptions = (row) => {
    const current = row?.rqst_status;
    const allowed = getAllowedStatusOptions(row);
    const merged = [current, ...allowed].filter(Boolean);
    return Array.from(new Set(merged));
  };

  // Proper Syncfusion custom editor (prevents "col.edit.create is not a function")
  const statusEdit = useMemo(() => {
    let dropdownObj;

    return {
      create: () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'text');
        return input;
      },
      write: (args) => {
        const row = args?.rowData || {};
        const options = getStatusDropdownOptions(row);
        const dataSource = options.map((value) => ({
          value,
          text: getStatusText(value),
        }));
        const currentValue =
          row?.rqst_status ||
          getStatusValueFromText(row?.statusText) ||
          options[0];

        dropdownObj = new DropDownList({
          dataSource,
          fields: { text: 'text', value: 'value' },
          value: currentValue,
          popupHeight: '220px',
          enableRtl: true,
          allowFiltering: true,
        });

        dropdownObj.appendTo(args.element);
      },
      read: () => dropdownObj?.text,
      destroy: () => {
        if (dropdownObj) {
          dropdownObj.destroy();
          dropdownObj = null;
        }
      },
    };
  }, [getStatusDropdownOptions, getStatusText, getStatusValueFromText]);

  const gridData = useMemo(() => {
    return (requestsList || []).map((r) => ({
      ...r,
      // flatten so Syncfusion doesn't depend on nested field support
      officeOwner: r?.user?.ofc_owner || '',
      statusText: getStatusText(r?.rqst_status || ''),
    }));
  }, [requestsList, getStatusText]);

  useEffect(() => {
    if (!authToken) return;
    // Backend rule: ADMIN/OWNER/STAFF must send selected dates. OFFICE can omit.
    dispatch(fetchRequests(isPrivileged ? { startDate, endDate } : {}));
  }, [authToken, dispatch, role, startDate, endDate, isPrivileged]);

  useEffect(() => {
    if (!authToken) return;
    // Only privileged roles can fetch offices list from /api/users
    if (isPrivileged) dispatch(fetchUsers());
  }, [authToken, dispatch, role, isPrivileged]);

  useEffect(() => {
    if (!authToken) return;
    // Ensure office list is available when opening the create modal for privileged roles
    if (!isCreateDialogOpen) return;
    if (isPrivileged) dispatch(fetchUsers());
  }, [authToken, dispatch, role, isCreateDialogOpen, isPrivileged]);

  useEffect(() => {
    if (!error) return;
    const rawMessage = typeof error === 'string' ? error : (error?.message || error?.error);
    const translated = translateBackendMessage(rawMessage);
    const fallback = t('notifications.frontend.dashboard.fetchRequestsFailed');
    message.error(translated || rawMessage || fallback);
  }, [error, t]);

  const toolbarOptions = useMemo(() => {
    const canEditStatus = role === 'OFFICE' || role === 'ADMIN' || role === 'STAFF' || role === 'OWNER';
    const options = [];
    if (canEditStatus) {
      options.push('Edit', 'Update', 'Cancel');
    }
    options.push('ExcelExport');
    return options;
  }, [role]);

  const handleToolbarClick = (args) => {
    if (args?.item?.id && args.item.id.includes('excelexport')) {
      gridRef.current?.excelExport?.();
    }
  };

  const editSettings = useMemo(() => ({
    allowEditing: true,
    allowAdding: false,
    allowDeleting: false,
    // mode: 'Dialog',
  }), []);

  const filterSettings = useMemo(() => ({ type: 'Excel' }), []);
  const selectionSettings = useMemo(() => ({
    type: 'Multiple',
    checkboxMode: 'Default',
    persistSelection: true,
  }), []);

  const officeOptions = useMemo(() => {
    // We want to show offices by their `ofc_owner` value (frontend field: officeId).
    // Deduplicate by officeId, but keep an officeUserUuid (id) to send to backend.
    const byOfficeId = new Map();
    (usersList || [])
      .filter((u) => u?.role === 'OFFICE')
      .forEach((u) => {
        const id = u?.id || u?.uuid; // usr_uuid
        // Some screens use `officeOwner` while API uses `officeId`
        const officeId = (u?.officeOwner || u?.officeId || '').trim(); // ofc_owner
        if (!id || !officeId) return;
        if (!byOfficeId.has(officeId)) {
          byOfficeId.set(officeId, { id, officeId, display: officeId });
        }
      });
    return Array.from(byOfficeId.values());
  }, [usersList]);

  const officeOptionIds = useMemo(() => new Set((officeOptions || []).map((o) => o.id)), [officeOptions]);

  async function actionBegin(args) {
    if (args.requestType === 'beginEdit') {
      const row = args.rowData || args.data || {};
      const allowed = getAllowedStatusOptions(row);

      // Capture original status for reliable validation on save
      if (row?.rqst_sn) {
        originalStatusByRqstSnRef.current[row.rqst_sn] = row.rqst_status;
      }

      // If no allowed transitions, block editing completely
      if (!allowed.length) {
        args.cancel = true;
        if (role === 'OFFICE') message.warning(t('notifications.frontend.dashboard.officeCanCancelOnlyOpen'));
        else message.warning(t('notifications.frontend.dashboard.cannotEditCancelled'));
        return;
      }
      return;
    }

    if (args.requestType !== 'save') return;

    // prevent Grid from updating locally; server is source of truth
    args.cancel = true;

    const row = args.data || {};
    const newStatus = getStatusValueFromText(row?.statusText || row?.rqst_status);
    const storedPrevStatus = row?.rqst_sn ? originalStatusByRqstSnRef.current[row.rqst_sn] : undefined;
    const prev = args.previousData || {};
    const prevStatus = storedPrevStatus ?? prev?.rqst_status ?? args.rowData?.rqst_status;

    // UI-side rules (backend enforces too)
    if (role === 'OFFICE') {
      const ok =
        (prevStatus === 'OPEN' && newStatus === 'CANCELLED') ||
        (prevStatus === 'RESPONSE' && newStatus === 'APPROVED');

      if (!ok) {
        message.error(t('notifications.frontend.dashboard.officeStatusTransitionOnly'));
        return;
      }
    } else if (role === 'ADMIN' || role === 'STAFF' || role === 'OWNER') {
      if (prevStatus === 'CANCELLED') {
        message.error(t('notifications.frontend.dashboard.cannotChangeCancelledStatus'));
        return;
      }
    } else {
      message.error(t('notifications.frontend.dashboard.noPermissionChangeStatus'));
      return;
    }

    try {
      await dispatch(updateRequestStatus({ rqstSn: row.rqst_sn, status: newStatus })).unwrap();
      message.success(t('notifications.frontend.dashboard.statusUpdated'));
      const isPrivileged = role === 'ADMIN' || role === 'OWNER' || role === 'STAFF';
      dispatch(fetchRequests(isPrivileged ? { startDate, endDate } : {}));
      gridRef.current?.closeEdit?.();
      if (row?.rqst_sn) delete originalStatusByRqstSnRef.current[row.rqst_sn];
    } catch (e) {
      const msg = typeof e === 'string' ? e : (e?.message || e?.error);
      const translated = translateBackendMessage(msg);
      message.error(translated || msg || t('notifications.frontend.dashboard.statusUpdateFailed'));
    }
  }

  const commandClick = (args) => {
    if (args.commandColumn.type === 'Update Response') {
      setSelectedRqstSn(args.rowData?.rqst_sn);
      openModalWithDelay(setIsResponseDialogOpen);
    } else if (args.commandColumn.type === 'Request Files') {
      const rqstSn = args.rowData?.rqst_sn;
      setSelectedRqstSn(rqstSn);
      if (rqstSn) {
        dispatch(fetchRequestFiles({ rqstSn }));
      }
      openModalWithDelay(setIsRequestFilesDialogOpen);
    } else if (args.commandColumn.type === 'View Signature') {
      const rqstSn = args.rowData?.rqst_sn;
      setSelectedRqstSn(rqstSn);
      openModalWithDelay(setIsSignatureDialogOpen);
      openSignaturePreview(rqstSn);
    }
  };

  const rowDataBound = (args) => {
    if (!isPrivileged) return;
    const dup = Number(args?.data?.request_duplicate || 0);
    if (!args?.row) return;
    args.row.classList.remove('rqst-dup-same-office', 'rqst-dup-diff-office');
    args.row.removeAttribute('title');
    if (dup === 1) {
      args.row.classList.add('rqst-dup-same-office');
      args.row.setAttribute(
        'title',
        t('requests.duplicateSameOffice', { defaultValue: 'Duplicate – Same Office' })
      );
    }
    if (dup === 2) {
      args.row.classList.add('rqst-dup-diff-office');
      args.row.setAttribute(
        'title',
        t('requests.duplicateDifferentOffice', { defaultValue: 'Duplicate – Different Office' })
      );
    }
  };

  const bulkUpdateStatus = async (status) => {
    const rows = gridRef.current?.getSelectedRecords?.() || [];
    const targets = rows.filter((row) => row?.rqst_sn);
    if (!targets.length) {
      message.warning(t('requests.bulkSelectFirst', { defaultValue: 'Select one or more rows first' }));
      return;
    }

    const results = await Promise.allSettled(
      targets.map((row) => dispatch(updateRequestStatus({ rqstSn: row.rqst_sn, status })).unwrap())
    );

    const successCount = results.filter((result) => result.status === 'fulfilled').length;
    const failedCount = results.length - successCount;

    if (successCount) {
      message.success(
        t('requests.bulkStatusUpdated', {
          defaultValue: `Updated ${successCount} requests`,
          count: successCount,
        })
      );
    }
    if (failedCount) {
      message.error(
        t('requests.bulkStatusFailed', {
          defaultValue: `Failed to update ${failedCount} requests`,
          count: failedCount,
        })
      );
    }

    dispatch(fetchRequests(isPrivileged ? { startDate, endDate } : {}));
    gridRef.current?.clearSelection?.();
  };

  return (
    <>
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>{t('dashboard.title')}</h1>

        <div className={styles.actions}>
            <RangePicker
              value={dateRange}
              onChange={(values) => {
                if (values?.[0] && values?.[1]) setDateRange(values);
              }}
            />
            {(role === 'OFFICE' || isPrivileged) && (
              <Button
                style={{ margin: '0 10px', fontWeight: '500'}}
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => openModalWithDelay(setIsCreateDialogOpen)}
              >
                {t('common.add')}
              </Button>
            )}
            <Button
              style={{ marginLeft: '10px', fontWeight: '500'}}
              icon={<ReloadOutlined/>}
              onClick={() => {
                const isPrivileged = role === 'ADMIN' || role === 'OWNER' || role === 'STAFF';
                dispatch(fetchRequests(isPrivileged ? { startDate, endDate } : {}));
              }}
            >
              {t('common.refresh')}
            </Button>
            <Button danger
                  style={{ marginLeft: '10px', fontWeight: '500'}}
                  onClick={() => bulkUpdateStatus('APPROVED')}
                  icon={<CheckCircleOutlined />}
                >
                  {t('requests.bulkApprove', { defaultValue: 'Approve selected' })}
            </Button>
            {isPrivileged && (
              <>
                <Button
                  color="geekblue" variant="outlined"
                  style={{ marginLeft: '10px', fontWeight: '500'}}
                  onClick={() => bulkUpdateStatus('PAID')}
                  icon={<DollarOutlined />}
                >
                  {t('requests.bulkPaid', { defaultValue: 'Mark selected as paid' })}
                </Button>
              </>
            )}

            {/* <Button
              icon={<UploadOutlined/>}
              onClick={() => {
                openModalWithDelay(setIsResponseDialogOpen);
              }}
            >
              {t('dashboard.newResponse')}
            </Button>

            <Button
              onClick={() => {dispatch(axios.get('/files/c3a68a47-5279-4d47-94fe-ac48173070ea/url')).then((response) => {
                console.log(response.data);
              }).catch((error) => {
                console.error(error);
              });
              }}
            >
              try
            </Button> */}
        </div>
        </div>

        {isPrivileged && (
          <div className={styles.duplicateLegend}>
            {/* <span className={styles.duplicateLegendTitle}>
              {t('requests.duplicateLegend', { defaultValue: 'Duplicate rows:' })}
            </span> */}
            <div className={styles.duplicateLegendItem}>
              <span
                className={`${styles.duplicateLegendSwatch} ${styles.duplicateLegendSwatchSame}`}
                aria-hidden="true"
              />
              <span>{t('requests.duplicateSameOffice', { defaultValue: 'Duplicate – Same Office' })}</span>
            </div>
            <div className={styles.duplicateLegendItem}>
              <span
                className={`${styles.duplicateLegendSwatch} ${styles.duplicateLegendSwatchDiff}`}
                aria-hidden="true"
              />
              <span>{t('requests.duplicateDifferentOffice', { defaultValue: 'Duplicate – Different Office' })}</span>
            </div>
          </div>
        )}

        <div className="control-pane pdfviewer-document-list">
          <div id="targetElement" className="control-section col-lg-12 defaultDialogComponent dialog-target">
            <GridComponent 
              id="requestsGrid"
              loading={loading}
              dataSource={gridData}
              locale={i18n?.language || 'he'}
              ref={gridRef}
              toolbar={toolbarOptions}
              toolbarClick={handleToolbarClick}
              editSettings={editSettings}
              actionBegin={actionBegin}
              commandClick={commandClick}
              rowDataBound={rowDataBound}
              allowSelection={true}
              selectionSettings={selectionSettings}
              allowExcelExport={true}
              allowFiltering={true}
              filterSettings={filterSettings}
              allowPaging={true}
              pageSettings={{ pageSize: 10 }}
              enableRtl={true}
              allowSorting={true}
            >
              <ColumnsDirective>
                <ColumnDirective type="checkbox" width="20" />
                <ColumnDirective field="rqst_sn" visible={false} isPrimaryKey={true} />
                <ColumnDirective 
                  headerText={t('requests.number') || 'Request #'} 
                  textAlign='Center' 
                  field='rqst_id'
                  width='110'
                  allowEditing={false}
                />
                {isPrivileged && (
                  <ColumnDirective 
                    headerText={t('requests.officeOwner') || 'Office Owner'} 
                    textAlign='Center' 
                    field='officeOwner'
                    width='140'
                    valueAccessor={officeOwnerValueAccessor}
                    allowEditing={false}
                  />
                )}
                <ColumnDirective 
                  headerText={t('requests.date') || 'Created'} 
                  textAlign='Center' 
                  field='createdAt'
                  width='160'
                  valueAccessor={israelDateValueAccessor}
                  allowEditing={false}
                />
                <ColumnDirective 
                  headerText={t('requests.customer') || 'Name'} 
                  textAlign='Center' 
                  field='rqst_prsn_first'
                  width='140'
                  valueAccessor={fullNameValueAccessor}
                  allowEditing={false}
                />
                <ColumnDirective 
                  headerText={t('requests.id') || 'Person ID'} 
                  textAlign='Center' 
                  field='rqst_prsn_id'
                  width='140'
                  allowEditing={false}
                />
                <ColumnDirective 
                  headerText={t('requests.address') || 'Address'} 
                  textAlign='Center' 
                  field='rqst_prsn_address'
                  width='140'
                  allowEditing={false}
                />
                {/* {isPrivileged && (
                  <ColumnDirective
                    headerText={t('requests.duplicate') || 'Duplicate'}
                    textAlign="Center"
                    field="request_duplicate"
                    width="200"
                    valueAccessor={duplicateValueAccessor}
                    allowEditing={false}
                  />
                )} */}
                <ColumnDirective 
                  headerText={t('requests.status') || 'Status'} 
                  textAlign='Center' 
                  field='statusText'
                  width='120'
                  edit={statusEdit}
                />

                <ColumnDirective headerText={t('requests.actions')} width='120' textAlign="Center" commands={!isPrivileged ? officeCommands : commands}/>
                
                {/* <ColumnDirective headerText={t('dashboard.files')} width='60' textAlign="Center" field='files' /> */}

              </ColumnsDirective>
              <Inject services={[Page, Edit, Toolbar, Filter, Sort, CommandColumn, ExcelExport, Selection]} />
            </GridComponent>
          </div>
        </div>
        
      </div>

      <Modal
        title={t('requests.createTitle') || 'Create Request'}
        open={isCreateDialogOpen}
        onCancel={() => {
          setIsCreateDialogOpen(false);
          createForm.resetFields();
        }}
        okText={t('common.add') || 'Add'}
        cancelText={t('common.cancel') || 'Cancel'}
        confirmLoading={creating || isSubmittingCreateRequest}
        okButtonProps={{
          disabled:
            creating ||
            isSubmittingCreateRequest ||
            (isPrivileged && officeOptions.length === 0),
        }}
        onOk={async () => {
          if (createRequestSubmitLockRef.current) return;
          createRequestSubmitLockRef.current = true;
          setIsSubmittingCreateRequest(true);
          try {
            const values = await createForm.validateFields();
            const payload = {
              rqst_prsn_id: values.rqst_prsn_id,
              rqst_prsn_first: values.rqst_prsn_first,
              rqst_prsn_last: values.rqst_prsn_last,
              rqst_prsn_address: values.rqst_prsn_address || undefined,
              rqst_status: 'OPEN',
              rqst_prmsn_date: values.rqst_prmsn_date,
              ...(isPrivileged ? { officeUserUuid: values.officeUserUuid } : {}),
            };
            const signatureFile = getSignatureFileFromList(values.signatureFile);

            // Check warnings only on submit
            const warningsRes = await dispatch(fetchRequestWarnings({
              personId: payload.rqst_prsn_id,
              officeUserUuid: payload.officeUserUuid,
            })).unwrap();

            const w = warningsRes?.data;

            if (w?.isBlocked) {
              Modal.error({
                title: t('requests.duplicateBlockedTitle', { defaultValue: 'Duplicate request' }),
                content: t('requests.duplicateBlocked', {
                  days: w.blockDays,
                  rqstId: w.blockingRequest?.rqst_id,
                  defaultValue: 'A request for this person ID was already created in the last {{days}} days (request #{{rqstId}}). A new request cannot be created.',
                }),
              });
              return;
            }

            if (w?.hasRecentRequest) {
              // Default is NO (cancel focused). If user presses No -> do not create.
              await new Promise((resolve, reject) => {
                Modal.confirm({
                  title: t('requests.warningTitle', { defaultValue: 'Warning' }),
                  content: t('requests.recentRequestWarning', {
                    rqstId: w.recentRequest?.rqst_id,
                    days: w.warnDays,
                    defaultValue: 'There is a request for this person ID within the last {{days}} days (request #{{rqstId}}). Want to add anyway?',
                  }),
                  okText: t('common.yes', { defaultValue: 'Yes' }),
                  cancelText: t('common.no', { defaultValue: 'No' }),
                  autoFocusButton: 'cancel',
                  // Make "No" the primary (blue) button
                  okButtonProps: { type: 'default' },
                  cancelButtonProps: { type: 'primary' },
                  onOk: () => resolve(true),
                  onCancel: () => reject(new Error('cancelled')),
                });
              });
            }

            await dispatch(createRequest({ payload, signatureFile })).unwrap();

            message.success(t('requests.created') || 'Request created');
            setIsCreateDialogOpen(false);
            createForm.resetFields();

            // refresh grid
            dispatch(fetchRequests(isPrivileged ? { startDate, endDate } : {}));
          } catch (e) {
            // validation errors are handled by form
            if (e?.errorFields) return;
            if (e?.message === 'cancelled') return; // user chose No in warning confirm
            const msg = typeof e === 'string' ? e : (e?.message || e?.error);
            const translated = translateBackendMessage(msg);
            message.error(translated || msg || t('notifications.frontend.dashboard.createRequestFailed'));
          } finally {
            setIsSubmittingCreateRequest(false);
            createRequestSubmitLockRef.current = false;
          }
        }}
      >
        <Form form={createForm} layout="vertical">
          {isPrivileged && (
            <Form.Item
              label={t('requests.officeOwner') || 'Office'}
              name="officeUserUuid"
              rules={[{ required: true, message: t('validation.required') || 'Required' }]}
              style={{ marginTop: 4, marginBottom: 8 }}
            >
              <ComboBoxComponent
                dataSource={officeOptions}
                fields={{ text: 'display', value: 'id' }}
                placeholder={t('requests.officeOwner') || 'Office'}
                allowFiltering={true}
                allowCustom={false}
                enableRtl={true}
                enabled={!usersLoading}
                noRecordsTemplate={t('requests.noOfficesWithOwner') || 'No offices found (missing ofc_owner). Please set Office Owner in Offices screen.'}
                // Only write to the form when the user actually selected an item.
                // While typing/clearing, Syncfusion may emit events with null itemData.
                change={(e) => {
                  if (e?.value == null) {
                    createForm.setFieldValue('officeUserUuid', undefined);
                    return;
                  }
                  // e.value is the selected record "id" because fields.value = 'id'
                  if (typeof e.value === 'string' && officeOptionIds.has(e.value)) {
                    createForm.setFieldValue('officeUserUuid', e.value);
                  }
                }}
              />
            </Form.Item>
          )}
          <Form.Item
            label={t('requests.personId') || 'Person ID'}
            name="rqst_prsn_id"
            normalize={(value) => String(value || '').replace(/\D/g, '').slice(0, 9)}
            validateFirst
            rules={[
              { required: true, message: t('validation.required') || 'Required' },
              {
                len: 9,
                message: t('validation.idLength', { defaultValue: 'ID must be exactly 9 digits' }),
              },
              {
                validator: async (_, value) => {
                  if (value == null || String(value).trim() === '') return Promise.resolve();
                  return isValidIsraeliId(value)
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('validation.invalidIsraeliId') || 'Invalid ID'));
                },
              },
            ]}
            style={{ marginTop: 4, marginBottom: 8 }}
          >
            <Input maxLength={9} inputMode="numeric" pattern="[0-9]*" />
          </Form.Item>
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item
                label={t('requests.firstName') || 'First name'}
                name="rqst_prsn_first"
                rules={[{ required: true, message: t('validation.required') || 'Required' }]}
                style={{ marginTop: 4, marginBottom: 8 }}
              >
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('requests.lastName') || 'Last name'}
                name="rqst_prsn_last"
                rules={[{ required: true, message: t('validation.required') || 'Required' }]}
                style={{ marginTop: 4, marginBottom: 8 }}
              >
                <Input maxLength={100} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label={t('requests.address') || 'Address'}
            name="rqst_prsn_address"
            rules={[{ required: true, message: t('validation.required') || 'Required' }]}
            style={{ marginTop: 4, marginBottom: 8 }}
          >
            <Input.TextArea maxLength={300} rows={2} />
          </Form.Item>

          <Form.Item
            name="approvedByOffice"
            valuePropName="checked"
            initialValue={false}
            rules={[
              {
                validator: (_, value) => (
                  value
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('validation.required') || 'Required'))
                ),
              },
            ]}
            style={{ marginTop: 4, marginBottom: 8 }}
          >
            <Checkbox style={{ fontSize: '14px' }}>
              {t('requests.approvedByOffice') || 'Approved by Office'}
            </Checkbox>
          </Form.Item>

          <Form.Item
            name="signatureFile"
            label={t('requests.signatureUpload') || 'Client signature / power of attorney'}
            valuePropName="fileList"
            initialValue={[]}
            getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
            rules={[
              {
                validator: (_, fileList) => {
                  const file = getSignatureFileFromList(fileList);
                  if (!file) {
                    return Promise.reject(
                      new Error(t('requests.signatureRequired') || 'Signature file is required')
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
            extra={t('requests.signatureUploadTypes') || 'PDF, Word, or image (JPG, PNG)'}
            style={{ marginTop: 4, marginBottom: 8 }}
          >
            <Upload.Dragger
              name="signature"
              maxCount={1}
              accept={SIGNATURE_ACCEPT}
              className={styles.signatureDropzone}
              beforeUpload={(file) => {
                const ext = `.${String(file.name || '').split('.').pop() || ''}`.toLowerCase();
                if (!SIGNATURE_EXTENSIONS.includes(ext)) {
                  message.error(
                    t('requests.signatureInvalidType') || 'Only PDF, Word, or image files are allowed'
                  );
                  return Upload.LIST_IGNORE;
                }
                if (file.size > SIGNATURE_MAX_BYTES) {
                  message.error(
                    t('requests.signatureTooLarge') || 'File exceeds the 10MB limit'
                  );
                  return Upload.LIST_IGNORE;
                }
                return false;
              }}
            >
              <span className={styles.signatureDropzoneInner}>
                <UploadOutlined className={styles.signatureDropzoneIcon} />
                <span>
                  {t('requests.signatureUploadHint') || 'Drop a file here or click to select'}
                </span>
              </span>
            </Upload.Dragger>
          </Form.Item>

          <Form.Item
            style={{ marginTop: 4, marginBottom: 8, display: 'flex', alignItems: 'center' }}
          >
            <span style={{ marginRight: 8, minWidth: 120 }}>
              {t('requests.permissionDate') || 'Permission Date'}
            </span>
            <Form.Item
              name="rqst_prmsn_date"
              noStyle
              rules={[{ required: true, message: t('validation.required') || 'Required' }]}
            >
              <DatePicker format="DD/MM/YYYY" style={{ marginRight: 8 }} />
            </Form.Item>
          </Form.Item>

        </Form>
      </Modal>

      <Modal
        title={t('dashboard.uploadFile') || 'Upload Word, PDF, or Excel files'}
        open={isResponseDialogOpen}
        onCancel={() => {
          setIsResponseDialogOpen(false);
          resetResponseUploadState();
        }}
        okText={t('common.upload') || 'Upload'}
        cancelText={t('common.cancel') || 'Cancel'}
        okButtonProps={{ loading: isUploadingFiles }}
        onOk={async () => {
          setIsUploadingFiles(true);
          try {
            const formData = new FormData();
            if (currFiles) {
              currFiles.forEach((file) => {
                formData.append('files', file);
              });
            }
            await dispatch(uploadFiles({ rqstSn: selectedRqstSn, formData })).unwrap();
            message.success(t('dashboard.filesUploaded') || 'Files uploaded');
            setIsResponseDialogOpen(false);
            dispatch(fetchRequests(isPrivileged ? { startDate, endDate } : {}));
            resetResponseUploadState();
          } catch (e) {
            const msg = typeof e === 'string' ? e : (e?.message || e?.error);
            const translated = translateBackendMessage(msg);
            message.error(translated || msg || t('notifications.frontend.dashboard.uploadFilesFailed'));
          } finally {
            setIsUploadingFiles(false);
          }
        }}
      >
        <Input ref={responseFileInputRef} type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx" onChange={(e) => {
          const files = Array.from(e.target.files || []);
          setCurrFiles(files);
        }} />
      </Modal>

      <Modal
        title={t('requests.files') || 'Files'}
        open={isRequestFilesDialogOpen}
        onCancel={() => {
          setIsRequestFilesDialogOpen(false);
          dispatch(clearFiles());
          dispatch(clearDownloadUrl());
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsRequestFilesDialogOpen(false);
              dispatch(clearFiles());
              dispatch(clearDownloadUrl());
            }}
          >
            {t('common.cancel') || 'Cancel'}
          </Button>,
        ]}
      >
        <div>
          {Array.isArray(files) && files.some((file) => !isSignatureKind(file.kind)) ? (
            <div style={{ marginBottom: 10, fontSize: '14px', color: '#cc0000' }}>
              {t('notifications.frontend.dashboard.viewResponseToApproveRequest') || 'View response to approve request.'}
            </div>
          ) : null}

        <div>
          {Array.isArray(files) && files.length > 0 ? (
            files.map((file) => (
              <div key={file.uuid || file.originalName}>
                <span className={styles.fileKindLabel}>
                  {isSignatureKind(file.kind)
                    ? (t('requests.fileKindSignature') || 'Signature')
                    : (t('requests.fileKindResponse') || 'Response')}
                </span>
                <span>{file.originalName || 'File'}</span>
                <Button
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={() => handleRequestFileDownload(file)}
                />
                {isAdminOrOwner && (
                <Button
                  type="link"
                  danger
                  icon={<CloseOutlined />}
                  onClick={() => {
                    Modal.confirm({
                      title: t('requests.deleteFileTitle') || 'Delete file',
                      content: t('requests.deleteFileConfirm') || 'Are you sure you want to delete this file?',
                      okText: t('common.delete') || 'Delete',
                      cancelText: t('common.cancel') || 'Cancel',
                      okButtonProps: { danger: true },
                      onOk: () => handleRequestFileDelete(file),
                    });
                  }}
                />
                )}
              </div>
            ))
          ) : (
            <div>{t('requests.noFiles') || 'No files found'}</div>
          )}
        </div>
        </div>
      </Modal>

      <Modal
        title={t('requests.signaturePreviewTitle') || 'Signature'}
        open={isSignatureDialogOpen}
        onCancel={() => {
          setIsSignatureDialogOpen(false);
          resetSignaturePreview();
        }}
        width={720}
        footer={[
          signaturePreview?.uuid ? (
            <Button
              key="download"
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => handleSignatureDownload(signaturePreview)}
            >
              {t('common.download') || 'Download'}
            </Button>
          ) : null,
          <Button
            key="close"
            onClick={() => {
              setIsSignatureDialogOpen(false);
              resetSignaturePreview();
            }}
          >
            {t('common.close') || 'Close'}
          </Button>,
        ]}
      >
        {signaturePreviewLoading || filesLoading ? (
          <div className={styles.signatureLoading}>
            <Spin />
          </div>
        ) : signaturePreview?.url && isImageFileName(signaturePreview.name) ? (
          <img
            src={signaturePreview.url}
            alt={signaturePreview.name || ''}
            className={styles.signaturePreview}
          />
        ) : signaturePreview?.url && isPdfFileName(signaturePreview.name) ? (
          <iframe
            title={signaturePreview.name || 'signature'}
            src={signaturePreview.url}
            className={styles.signaturePdf}
          />
        ) : signaturePreview ? (
          <div>
            <div>{signaturePreview.name}</div>
            <div className={styles.signatureHint}>
              {t('requests.signaturePreviewUnavailable') || 'Preview is available for images and PDF files. Download the file to view it.'}
            </div>
          </div>
        ) : (
          <div>{t('requests.noSignature') || 'No signature file found'}</div>
        )}
      </Modal>

    </>
  );
};

export default Dashboard; 